import { useState, useCallback } from 'react'
import { 
  generateCollage, 
  generateCollageFile, 
  downloadImage,
  type CollageLayout,
  type CollageOptions,
  type CollageConfig,
  XHS_SIZES
} from '@/lib/collage'
import { useAssets } from './useAssets'
import { toast } from 'sonner'
import { captureError } from '@/lib/sentry.tsx'

// ============================================================
// 类型定义
// ============================================================

interface CollageState {
  isGenerating: boolean
  previewUrl: string | null
  error: string | null
}

interface UseCollageReturn extends CollageState {
  // 生成拼图预览
  generatePreview: (images: string[], layout: CollageLayout, options?: CollageOptions) => Promise<string>
  // 生成拼图文件
  generateFile: (images: string[], layout: CollageLayout, filename: string, options?: CollageOptions) => Promise<File>
  // 下载拼图
  downloadCollage: (filename?: string) => void
  // 保存拼图到素材库
  saveToAssets: (filename?: string) => Promise<string | null>
  // 清除预览
  clearPreview: () => void
}

// ============================================================
// Hook 实现
// ============================================================

export function useCollage(projectId?: string): UseCollageReturn {
  const [isGenerating, setIsGenerating] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // 如果提供了 projectId，可以保存到素材库
  const { addAsset } = projectId ? useAssets(projectId) : { addAsset: null }

  /**
   * 生成拼图预览
   */
  const generatePreview = useCallback(async (
    images: string[], 
    layout: CollageLayout, 
    options?: CollageOptions
  ): Promise<string> => {
    setIsGenerating(true)
    setError(null)

    try {
      const config: CollageConfig = {
        layout,
        images,
        options: {
          ...XHS_SIZES.portrait,
          format: 'jpeg',
          quality: 0.9,
          ...options,
        },
      }

      const dataUrl = await generateCollage(config)
      setPreviewUrl(dataUrl)
      toast.success('拼图生成成功')
      return dataUrl
    } catch (err) {
      captureError(err, { feature: 'collage', action: 'generate-preview', layout, imageCount: images.length })
      const message = err instanceof Error ? err.message : '拼图生成失败'
      setError(message)
      toast.error(message)
      throw err
    } finally {
      setIsGenerating(false)
    }
  }, [])

  /**
   * 生成拼图文件
   */
  const generateFile = useCallback(async (
    images: string[], 
    layout: CollageLayout, 
    filename: string,
    options?: CollageOptions
  ): Promise<File> => {
    setIsGenerating(true)
    setError(null)

    try {
      const config: CollageConfig = {
        layout,
        images,
        options: {
          ...XHS_SIZES.portrait,
          format: 'jpeg',
          quality: 0.95,
          ...options,
        },
      }

      const file = await generateCollageFile(config, filename)
      return file
    } catch (err) {
      captureError(err, { feature: 'collage', action: 'generate-file', layout, imageCount: images.length })
      const message = err instanceof Error ? err.message : '拼图生成失败'
      setError(message)
      throw err
    } finally {
      setIsGenerating(false)
    }
  }, [])

  /**
   * 下载当前预览的拼图
   */
  const downloadCollage = useCallback((filename = '拼图.jpg') => {
    if (!previewUrl) {
      toast.error('请先生成拼图')
      return
    }
    downloadImage(previewUrl, filename)
    toast.success('开始下载')
  }, [previewUrl])

  /**
   * 保存拼图到项目素材库
   * 需要先上传到 Uploadthing
   */
  const saveToAssets = useCallback(async (filename = '拼图.jpg'): Promise<string | null> => {
    if (!previewUrl) {
      toast.error('请先生成拼图')
      return null
    }

    if (!addAsset) {
      toast.error('未指定项目，无法保存')
      return null
    }

    try {
      // TODO: 上传到 Uploadthing
      // 目前先用本地 URL 占位
      const assetId = await addAsset({
        filename,
        url: previewUrl,  // 实际应该是 Uploadthing URL
        type: 'combined',
        description: '拼图素材',
      })

      toast.success('已保存到素材库')
      return assetId
    } catch (err) {
      const message = err instanceof Error ? err.message : '保存失败'
      toast.error(message)
      return null
    }
  }, [previewUrl, addAsset])

  /**
   * 清除预览
   */
  const clearPreview = useCallback(() => {
    setPreviewUrl(null)
    setError(null)
  }, [])

  return {
    isGenerating,
    previewUrl,
    error,
    generatePreview,
    generateFile,
    downloadCollage,
    saveToAssets,
    clearPreview,
  }
}

// ============================================================
// 辅助 Hook：快速拼图
// ============================================================

/**
 * 快速生成特定布局的拼图
 */
export function useQuickCollage() {
  const { generatePreview, isGenerating, previewUrl } = useCollage()

  const create2x2 = useCallback((images: string[]) => {
    if (images.length < 4) {
      toast.error('四宫格需要 4 张图片')
      return Promise.reject(new Error('需要 4 张图片'))
    }
    return generatePreview(images.slice(0, 4), '2x2')
  }, [generatePreview])

  const create3x3 = useCallback((images: string[]) => {
    if (images.length < 9) {
      toast.error('九宫格需要 9 张图片')
      return Promise.reject(new Error('需要 9 张图片'))
    }
    return generatePreview(images.slice(0, 9), '3x3')
  }, [generatePreview])

  const create3x1 = useCallback((images: string[]) => {
    if (images.length < 3) {
      toast.error('横向三联需要 3 张图片')
      return Promise.reject(new Error('需要 3 张图片'))
    }
    return generatePreview(images.slice(0, 3), '3x1')
  }, [generatePreview])

  return {
    isGenerating,
    previewUrl,
    create2x2,
    create3x3,
    create3x1,
  }
}

